/**
 * Typed fetch client for the Omakase Vault API.
 */

export interface ApiOk<T> { ok: true; data: T }
export interface ApiErr { ok: false; error: { code: string; message: string } }

export type ApiResponse<T> = ApiOk<T> | ApiErr;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// Global signal raised by any 401 from an authenticated endpoint. The
// SPA shell hooks into this to wipe localStorage + redirect to /login.
// Without this, the user keeps seeing decrypted vault items even after
// the server has revoked their session (e.g. admin revoked the device).
type SessionExpiredHook = () => void;
let sessionExpiredHook: SessionExpiredHook | null = null;
export function onSessionExpired(fn: SessionExpiredHook): void { sessionExpiredHook = fn; }

async function call<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "same-origin",
  };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let parsed: ApiResponse<T> | null = null;
  try { parsed = text ? (JSON.parse(text) as ApiResponse<T>) : null; } catch {
    throw new ApiError(res.status, "parse_error", text || `HTTP ${res.status}`);
  }
  if (res.status === 401 && parsed?.ok === false
      && (parsed.error.code === "session_missing" || parsed.error.code === "session_expired" || parsed.error.code === "session_revoked")) {
    // Fire the hook BEFORE throwing so the SPA can wipe + redirect even
    // when the caller doesn't catch the error.
    sessionExpiredHook?.();
  }
  if (parsed && parsed.ok === false) {
    throw new ApiError(res.status, parsed.error.code, parsed.error.message);
  }
  if (parsed && parsed.ok === true) return parsed.data;
  throw new ApiError(res.status, "unknown", `HTTP ${res.status}`);
}

export const api = {
  // Public
  genesisStatus: () => call<{ genesis_completed: boolean }>("GET", "/api/auth/genesis-status"),
  kdfParams: (email: string) =>
    call<{ kdf_algo: string; kdf_params: { algo: string; m: number; t: number; p: number; hashLength: number }; kdf_salt_b64: string }>(
      "GET",
      `/api/auth/kdf-params?email=${encodeURIComponent(email)}`,
    ),
  genesis: (body: Record<string, unknown>) => call<{ user_id: string; email: string; role: string }>("POST", "/api/auth/genesis", body),
  login: (body: Record<string, unknown>) => call<{ user: { id: string; email: string; name: string; role: string; must_change_pw: boolean }; wrapped: Record<string, string | null> }>("POST", "/api/auth/login", body),
  requestReset: (body: Record<string, unknown>) => call<{ ok: boolean }>("POST", "/api/auth/request-reset", body),
  confirmReset: (body: Record<string, unknown>) => call<{ ok: boolean }>("POST", "/api/auth/confirm-reset", body),
  inviteAccept: (body: Record<string, unknown>) => call<{ user: Record<string, string>; pre_seeded_items: { items_ct_b64: string; items_iv_b64: string | null } | null }>("POST", "/api/auth/invite-accept", body),
  inviteInfo: (id: string) => call<{ invite_id: string; email: string; role: string; expires_at: number; has_preseed: boolean }>("GET", `/api/auth/invite-info?id=${encodeURIComponent(id)}`),

  // Authenticated
  me: () => call<{ user: Record<string, unknown>; session: Record<string, unknown>; wrapped: Record<string, string | null> }>("GET", "/api/auth/me"),
  logout: () => call<{ revoked: boolean }>("POST", "/api/auth/logout"),
  changePassword: (body: Record<string, unknown>) => call<{ ok: boolean; must_relogin: boolean }>("POST", "/api/auth/change-password", body),

  // TOTP
  totpEnrollInit: () => call<{ secret_b32: string; otpauth_uri: string }>("POST", "/api/totp/enroll-init"),
  totpEnrollVerify: (code: string) => call<{ enrolled: boolean; enrolled_at: number }>("POST", "/api/totp/enroll-verify", { code }),
  totpDisable: (code: string) => call<{ disabled: boolean }>("POST", "/api/totp/disable", { code }),
  totpRecovery: (code: string) => call<{ ok: boolean; remaining: number }>("POST", "/api/totp/recovery", { code }),

  // Devices
  deviceRegisterInit: () => call<{ options: unknown }>("POST", "/api/devices/register-init"),
  deviceRegisterFinish: (response: unknown, deviceLabel?: string) => call<{ device_id: string; status: string }>("POST", "/api/devices/register-finish", { response, device_label: deviceLabel }),
  devicesList: (all = false) => call<{ devices: Array<Record<string, unknown>> }>("GET", `/api/devices/list${all ? "?all=1" : ""}`),
  deviceApprove: (device_id: string) => call<{ approved: boolean; path: string }>("POST", "/api/devices/approve", { device_id }),
  deviceRevoke: (device_id: string) => call<{ revoked: boolean }>("POST", "/api/devices/revoke", { device_id }),
  deviceRename: (device_id: string, name: string) => call<{ renamed: boolean }>("POST", "/api/devices/rename", { device_id, name }),
  deviceListAll: () => call<{ devices: Array<Record<string, unknown>> }>("GET", "/api/devices/list-all"),
  deviceRequestApproval: (device_id: string) => call<{ dispatched: boolean; via: string; email_to: string | null; expires_at: number; token_b64url: string | null }>("POST", "/api/devices/request-approval", { device_id }),
  deviceApproveByToken: (token_b64url: string) => call<{ approved: boolean; device_id: string }>("POST", "/api/devices/approve-token", { token_b64url }),

  // User block + SU reset + TOTP force-disable + item re-wrap
  userBlock: (id: string, blocked: boolean) => call<{ ok: boolean; blocked: boolean }>("POST", `/api/users/${id}/block`, { blocked }),
  userIssueResetLink: (id: string) => call<{ issued: boolean; expires_at: number; email_to: string }>("POST", `/api/users/${id}/issue-reset-link`),
  userForceDisableTotp: (id: string) => call<{ ok: boolean; was_enrolled: boolean }>("POST", `/api/users/${id}/force-disable-totp`),
  itemRewrap: (id: string, body: { wraps: Array<{ user_id: string; ephemeral_pub_jwk: string; wrapped_ck_b64: string }> }) =>
    call<{ rewrapped: boolean; version: number }>("POST", `/api/items/${id}/rewrap`, body),

  // WebAuthn assertion: public init returns allowed credentials for an
  // email before login. The actual verification happens inside POST
  // /api/auth/login via the webauthn_assertion_json field.
  assertionInitPublic: (email: string) => call<{ options: unknown }>("POST", "/api/devices/assertion-init-public", { email }),
  assertionInit: () => call<{ options: unknown }>("POST", "/api/devices/assertion-init"),

  // Recovery codes (full lifecycle)
  recoveryCodesStatus: () => call<{ remaining: number; total: number }>("GET", "/api/totp/recovery-codes/status"),
  recoveryCodesRegenerate: (current_code: string) => call<{ codes: string[] }>("POST", "/api/totp/recovery-codes/regenerate", { current_code }),

  // Sessions admin
  sessionsListAll: () => call<{ sessions: Array<Record<string, unknown>> }>("GET", "/api/sessions/list-all"),
  sessionRevoke: (session_id: string) => call<{ revoked: boolean }>("POST", "/api/sessions/revoke", { session_id }),

  // Items
  itemsList: () => call<{ items: Array<Record<string, unknown>> }>("GET", "/api/items"),
  itemsCreate: (body: Record<string, unknown>) => call<{ id: string; version: number }>("POST", "/api/items", body),
  // Note: body should include item_id (the client-generated ULID used to
  // build the ciphertext AAD). The server preserves this id verbatim.
  itemRead: (id: string) => call<{ item: Record<string, unknown>; wrap: { ephemeral_pub_jwk: string; wrapped_ck_b64: string } }>("GET", `/api/items/${id}`),
  itemUpdate: (id: string, body: Record<string, unknown>) => call<{ id: string; version: number }>("PATCH", `/api/items/${id}`, body),
  itemDelete: (id: string) => call<void>("DELETE", `/api/items/${id}`),
  itemRestore: (id: string) => call<{ restored: boolean }>("POST", `/api/items/${id}/restore`),
  itemHistory: (id: string) => call<{ history: Array<Record<string, unknown>> }>("GET", `/api/items/${id}/history`),

  // Users + recipients
  usersList: () => call<{ users: Array<Record<string, unknown>> }>("GET", "/api/users/list"),
  createInvite: (body: Record<string, unknown>) => call<{ invite_id: string; expires_at: number }>("POST", "/api/users/create-invite", body),
  provisionUser: (body: Record<string, unknown>) => call<{ user: { id: string; email: string; role: string; name: string }; must_change_pw: boolean }>("POST", "/api/users/provision", body),
  userPatch: (id: string, role: "ADMIN" | "EMPLOYEE") => call<{ id: string; role: string }>("PATCH", `/api/users/${id}`, { role }),
  userDelete: (id: string) => call<void>("DELETE", `/api/users/${id}`),
  recipients: () => call<{ super_user: Record<string, unknown> | null; recipients: Array<Record<string, unknown>> }>("GET", "/api/recipients/company"),

  // Useful links (SU/Admin CRUD, everyone reads)
  usefulLinksList: () => call<{ links: Array<{ id: string; label_en: string; label_jp: string | null; url: string; category: string | null; icon_name: string | null; order_index: number; created_at: number; updated_at: number }> }>("GET", "/api/useful-links"),
  usefulLinkCreate: (body: Record<string, unknown>) => call<{ id: string }>("POST", "/api/useful-links", body),
  usefulLinkUpdate: (id: string, body: Record<string, unknown>) => call<{ ok: boolean }>("PATCH", `/api/useful-links/${id}`, body),
  usefulLinkDelete: (id: string) => call<void>("DELETE", `/api/useful-links/${id}`),

  // Categories + audit
  categories: () => call<{ categories: Array<Record<string, unknown>> }>("GET", "/api/categories/list"),
  categoryUpdate: (id: string, body: { default_brand_color?: string; icon_name?: string }) =>
    call<{ ok: boolean }>("PATCH", `/api/categories/${id}`, body),
  audit: (params: { limit?: number; offset?: number; action?: string; item_id?: string; since?: number } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) qs.set(k, String(v));
    return call<{ rows: Array<Record<string, unknown>>; limit: number; offset: number }>("GET", `/api/audit/list?${qs.toString()}`);
  },
};
